import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { TOAST_ERROR, TOAST_SUCCESS, TOAST_WARNING } from '../../../utils/constants';
import {
  useCreateSectionMutation,
  useDeleteSectionMutation,
  useDeleteQuizSectionMutation,
  useRenameSectionMutation,
  useDuplicateSectionMutation,
  useCreatePageMutation,
  useUpdatePageMutation,
  useDeletePageMutation,
  useDuplicatePageMutation,
} from '../editCourseApi';
import { useQuizesActions } from '../../quizes/hooks/useQuizesActions';
import { useCourse, useCourseQuizPages } from '../../course/hooks';

/**
 * Hook for managing course structure actions (sections and pages)
 * Used by both TocMenu and VisualStructureEditor
 *
 * @param {number} courseId - The course ID
 * @param {Object} options - Optional configuration
 * @param {Function} options.onNavigateToPage - Callback to navigate to a page (receives pageId)
 * @param {Array} options.allPages - All pages in the course (for finding next page after deletion)
 * @param {Array} options.allSections - All sections in the course (for preventing last section deletion)
 * @param {number} options.currentPageId - Current page ID (for navigation after section deletion)
 * @returns {Object} Structure action handlers and dialog state
 */
export const useCourseStructureActions = (courseId, options = {}) => {
  const { onNavigateToPage, allPages = [], allSections = [], currentPageId } = options;
  const { t } = useTranslation();

  // Quiz hooks
  const course = useCourse();
  const quizPages = useCourseQuizPages();
  const { createQuiz } = useQuizesActions();

  // API mutations
  const [createSection] = useCreateSectionMutation();
  const [deleteSection] = useDeleteSectionMutation();
  const [deleteQuizSection] = useDeleteQuizSectionMutation();
  const [renameSection] = useRenameSectionMutation();
  const [duplicateSection] = useDuplicateSectionMutation();
  const [createPage] = useCreatePageMutation();
  const [updatePage] = useUpdatePageMutation();
  const [deletePage] = useDeletePageMutation();
  const [duplicatePage] = useDuplicatePageMutation();

  // Dialog state
  const [renameDialog, setRenameDialog] = useState({ open: false, type: null, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });
  const [isLoading, setIsLoading] = useState(false);

  // Inline editing state - tracks which item is being edited inline
  // { type: 'section' | 'page', id: number } or null
  const [inlineEditItem, setInlineEditItem] = useState(null);

  // ==================== SECTION ACTIONS ====================

  const handleAddSection = useCallback(async (afterSectionOrder = null, name = null) => {
    setIsLoading(true);
    try {
      const sectionName = name || t('editCourse.newSection');
      const order = afterSectionOrder !== null ? afterSectionOrder + 1 : 0;
      const sectionResult = await createSection({ courseId, name: sectionName, order }).unwrap();

      // Auto-create a page inside the new section and navigate to it
      if (sectionResult?.id) {
        const pageName = t('editCourse.newPage');
        const pageResult = await createPage({ sectionId: sectionResult.id, courseId, name: pageName, order: 0 }).unwrap();
        enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });

        // Navigate to the newly created page
        if (pageResult?.id && onNavigateToPage) {
          onNavigateToPage(pageResult.id);
        }
        return { section: sectionResult, page: pageResult };
      }

      enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
      return { section: sectionResult, page: null };
    } catch (error) {
      console.error('Failed to create section:', error);
      enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [courseId, createSection, createPage, t, onNavigateToPage]);

  // Start inline editing for a section
  const startInlineEditSection = useCallback((section) => {
    setInlineEditItem({ type: 'section', id: section.id });
  }, []);

  // Cancel inline editing
  const cancelInlineEdit = useCallback(() => {
    setInlineEditItem(null);
  }, []);

  // Legacy dialog opener (kept for backward compatibility)
  const openRenameSectionDialog = useCallback((section) => {
    // Use inline editing instead of dialog
    startInlineEditSection(section);
  }, [startInlineEditSection]);

  const handleRenameSection = useCallback(async (sectionId, newName) => {
    setIsLoading(true);
    try {
      await renameSection({ sectionId, courseId, name: newName }).unwrap();
      enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
      setRenameDialog({ open: false, type: null, item: null });
      setInlineEditItem(null);
    } catch (error) {
      console.error('Failed to rename section:', error);
      enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
    } finally {
      setIsLoading(false);
    }
  }, [courseId, renameSection, t]);

  const openDeleteSectionDialog = useCallback((section) => {
    if (allSections.length <= 1) {
      enqueueSnackbar(t('editCourse.cannotDeleteLastSection'), { variant: TOAST_WARNING });
      return;
    }
    setDeleteDialog({ open: true, type: 'section', item: section });
  }, [allSections, t]);

  const handleDeleteSection = useCallback(async (sectionId, sectionType = 'content') => {
    setIsLoading(true);
    try {
      // Check if current page is in the section being deleted
      const currentPageInSection = currentPageId && allPages.some(
        p => p.id === currentPageId && p.sectionId === sectionId
      );

      // Only find next page if we need to navigate away
      let nextPageId = null;
      if (currentPageInSection && onNavigateToPage) {
        // Get pages NOT in this section
        const pagesInOtherSections = allPages.filter(p => p.sectionId !== sectionId);
        if (pagesInOtherSections.length > 0) {
          // Navigate to the first page of another section
          nextPageId = pagesInOtherSections[0].id;
        }
      }

      // Quiz sections require a different API endpoint
      if (sectionType === 'quiz') {
        await deleteQuizSection({ sectionId, courseId }).unwrap();
      } else {
        await deleteSection({ sectionId, courseId }).unwrap();
      }
      enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
      setDeleteDialog({ open: false, type: null, item: null });

      // Navigate to a page in another section only if current page was deleted
      if (nextPageId && onNavigateToPage) {
        onNavigateToPage(nextPageId);
      }
    } catch (error) {
      console.error('Failed to delete section:', error);
      enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
    } finally {
      setIsLoading(false);
    }
  }, [courseId, deleteSection, deleteQuizSection, t, allPages, onNavigateToPage, currentPageId]);

  const handleDuplicateSection = useCallback(async (sectionId) => {
    setIsLoading(true);
    try {
      await duplicateSection({ sectionId, courseId }).unwrap();
      enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
    } catch (error) {
      console.error('Failed to duplicate section:', error);
      enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
    } finally {
      setIsLoading(false);
    }
  }, [courseId, duplicateSection, t]);

  // ==================== QUIZ ACTIONS ====================

  const handleAddQuiz = useCallback(async () => {
    if (!course?.id) return null;
    setIsLoading(true);

    try {
      const result = await createQuiz({
        courseId: course.id,
        name: t('quiz.newQuiz'),
        quizPages: quizPages || {},
        settings: course.settings || {},
        descriptionText: '',
        failText: '',
        successText: '',
        welcomeText: '',
        repeatable: false,
        threshold: 80,
        hideAnswers: false,
      });

      // Navigate to the newly created quiz page
      if (result?.pageId && onNavigateToPage) {
        onNavigateToPage(result.pageId);
      }
      return result;
    } catch (error) {
      console.error('Failed to create quiz:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [course, createQuiz, quizPages, t, onNavigateToPage]);

  // ==================== PAGE ACTIONS ====================

  const handleAddPage = useCallback(async (sectionId, afterPageOrder = null, name = null) => {
    setIsLoading(true);
    try {
      const pageName = name || t('editCourse.newPage');
      const order = afterPageOrder !== null ? afterPageOrder + 1 : 0;
      const result = await createPage({ sectionId, courseId, name: pageName, order }).unwrap();
      enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
      // Navigate to the newly created page
      if (result?.id && onNavigateToPage) {
        onNavigateToPage(result.id);
      }
      return result;
    } catch (error) {
      console.error('Failed to create page:', error);
      enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [courseId, createPage, t, onNavigateToPage]);

  // Start inline editing for a page
  const startInlineEditPage = useCallback((page) => {
    setInlineEditItem({ type: 'page', id: page.id });
  }, []);

  // Legacy dialog opener (kept for backward compatibility)
  const openRenamePageDialog = useCallback((page) => {
    // Use inline editing instead of dialog
    startInlineEditPage(page);
  }, [startInlineEditPage]);

  const handleRenamePage = useCallback(async (pageId, newName) => {
    setIsLoading(true);
    try {
      await updatePage({ pageId, courseId, name: newName }).unwrap();
      enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
      setRenameDialog({ open: false, type: null, item: null });
      setInlineEditItem(null);
    } catch (error) {
      console.error('Failed to rename page:', error);
      enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
    } finally {
      setIsLoading(false);
    }
  }, [courseId, updatePage, t]);

  const openDeletePageDialog = useCallback((page) => {
    if (allPages.length <= 1) {
      enqueueSnackbar(t('editCourse.cannotDeleteLastPage'), { variant: TOAST_WARNING });
      return;
    }
    setDeleteDialog({ open: true, type: 'page', item: page });
  }, [allPages, t]);

  const handleDeletePage = useCallback(async (pageId) => {
    setIsLoading(true);
    try {
      // Find the next page to navigate to before deleting
      let nextPageId = null;
      if (allPages.length > 1 && onNavigateToPage) {
        const pageIndex = allPages.findIndex(p => p.id === pageId);
        if (pageIndex !== -1) {
          // Try next page first, then previous
          if (pageIndex < allPages.length - 1) {
            nextPageId = allPages[pageIndex + 1].id;
          } else if (pageIndex > 0) {
            nextPageId = allPages[pageIndex - 1].id;
          }
        }
      }

      await deletePage({ pageId, courseId }).unwrap();
      enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
      setDeleteDialog({ open: false, type: null, item: null });

      // Navigate to the next available page
      if (nextPageId && onNavigateToPage) {
        onNavigateToPage(nextPageId);
      }
    } catch (error) {
      console.error('Failed to delete page:', error);
      enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
    } finally {
      setIsLoading(false);
    }
  }, [courseId, deletePage, t, allPages, onNavigateToPage]);

  const handleDuplicatePage = useCallback(async (pageId) => {
    setIsLoading(true);
    try {
      await duplicatePage({ pageId, courseId }).unwrap();
      enqueueSnackbar(t('globals.success'), { variant: TOAST_SUCCESS });
      // Note: API returns empty response, so we can't navigate to the duplicated page
    } catch (error) {
      console.error('Failed to duplicate page:', error);
      enqueueSnackbar(t('globals.error'), { variant: TOAST_ERROR });
    } finally {
      setIsLoading(false);
    }
  }, [courseId, duplicatePage, t]);

  // ==================== DIALOG HANDLERS ====================

  const closeRenameDialog = useCallback(() => {
    setRenameDialog({ open: false, type: null, item: null });
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog({ open: false, type: null, item: null });
  }, []);

  const handleRenameSubmit = useCallback((newName) => {
    if (!renameDialog.item) return;
    if (renameDialog.type === 'section') {
      handleRenameSection(renameDialog.item.id, newName);
    } else if (renameDialog.type === 'page') {
      handleRenamePage(renameDialog.item.id, newName);
    }
  }, [renameDialog, handleRenameSection, handleRenamePage]);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteDialog.item) return;
    if (deleteDialog.type === 'section') {
      // Pass section type for quiz sections to use correct API
      handleDeleteSection(deleteDialog.item.id, deleteDialog.item.type);
    } else if (deleteDialog.type === 'page') {
      handleDeletePage(deleteDialog.item.id);
    }
  }, [deleteDialog, handleDeleteSection, handleDeletePage]);

  return {
    // Section actions
    handleAddSection,
    openRenameSectionDialog,
    handleRenameSection,
    openDeleteSectionDialog,
    handleDeleteSection,
    handleDuplicateSection,

    // Quiz actions
    handleAddQuiz,

    // Page actions
    handleAddPage,
    openRenamePageDialog,
    handleRenamePage,
    openDeletePageDialog,
    handleDeletePage,
    handleDuplicatePage,

    // Inline editing
    inlineEditItem,
    startInlineEditSection,
    startInlineEditPage,
    cancelInlineEdit,

    // Dialog state and handlers (legacy, kept for delete dialogs)
    renameDialog,
    deleteDialog,
    closeRenameDialog,
    closeDeleteDialog,
    handleRenameSubmit,
    handleDeleteConfirm,
    isLoading,
  };
};

